import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Sparkles, ShieldCheck } from "lucide-react";
import { EditorialPage, handwritten } from "../components/editorial";
import AppDownload from "../components/AppDownload";
import { captureConversion } from "../lib/analytics";
import {
  BETA_LIVE,
  BETA_URL,
  TESTFLIGHT_URL,
  PLAY_BETA_URL,
} from "../lib/appLinks";

// /beta — the minimum-friction soft-launch landing (thread LL-0141).
// Brief (Adrien, 2026-06-23): get people into the beta in as few taps as
// possible, never a "you're on a waitlist" gate. When the TestFlight + Play
// open-testing links are live (BETA_LIVE), the QR + badges take over; until
// then the instant-access email capture is the path (we email the install link
// the moment it opens).

const perks = [
  {
    Icon: Zap,
    title: "In, in two taps",
    body: "Scan the code, install, you're in the beta. No forms, no approval queue, no waitlist.",
  },
  {
    Icon: Sparkles,
    title: "First to loop",
    body: "Help shape LetsLoop before anyone else. Your feedback goes straight to the founders.",
  },
  {
    Icon: ShieldCheck,
    title: "Real people, by default",
    body: "Real names, real photos, and a verification ladder that deepens with trust. The trust layer is on from day one.",
  },
];

const BetaPage = () => {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">(
    "idle",
  );

  useEffect(() => {
    document.title = "Join the LetsLoop beta";
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !marketingConsent) return;
    setState("loading");
    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          seekerType: "beta",
          interest: "beta_tester",
          // C-1 (LL-0028): assert explicit marketing consent so the strict
          // /api/subscribe gate adds the contact AND sends the install email.
          // Without these two fields the server records consented:false and
          // silently drops the signup (no audience-add, no email).
          marketingConsent: true,
          sourceForm: "beta_access",
          // Honeypot — real users never fill this (it's visually hidden).
          company: honeypot,
        }),
      });
      if (!res.ok) throw new Error("subscribe failed");
      setState("done");
      setEmail("");
      setMarketingConsent(false);
      captureConversion("beta_signup", { method: "beta_page_form" });
    } catch {
      setState("error");
    }
  };

  return (
    <EditorialPage>
      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-12 pb-24">
        {/* top bar (pages have no global navbar) */}
        <div className="flex items-center justify-between mb-12">
          <Link to="/" aria-label="LetsLoop home">
            <img
              src="/letsloop-logo.png"
              alt="LetsLoop"
              className="h-7 md:h-8 w-auto"
            />
          </Link>
          <Link
            to="/how-it-works"
            className="text-sm font-semibold text-ink/70 hover:text-salmon transition-colors"
          >
            How it works &rarr;
          </Link>
        </div>

        {/* hero */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div>
            <p
              className="text-3xl md:text-4xl text-salmon mb-3 -rotate-2 origin-left"
              style={handwritten}
            >
              the beta
            </p>
            <h1 className="font-serif text-ink tracking-tight leading-[0.95] text-5xl md:text-6xl mb-5">
              Be one of the{" "}
              <span className="italic text-salmon">first to loop.</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-md mb-9">
              {BETA_LIVE
                ? "Scan the code with your phone, or tap below. You're in the beta in two taps. No waitlist, no forms."
                : "We're opening the beta to a first group right now. Drop your email and we'll send your install link the moment it's live, days away. No waitlist."}
            </p>

            {BETA_LIVE && (
              <div className="mb-9">
                <AppDownload
                  showQr
                  showTrust
                  appStoreUrl={TESTFLIGHT_URL}
                  playStoreUrl={PLAY_BETA_URL}
                  qrHref={BETA_URL}
                  qrSrc="/beta-qr.png"
                  qrAlt="QR code to join the LetsLoop beta"
                  qrCaption="Scan to join the beta"
                  appStoreTopLine="Install via"
                  appStoreBottomLine="TestFlight"
                  playTopLine="Install via"
                  playBottomLine="Play Beta"
                />
              </div>
            )}

            {/* instant-access email capture (always available; the no-waitlist path) */}
            <form onSubmit={submit} className="max-w-md">
              {state === "done" ? (
                <div className="border-2 border-ink bg-paper-tan p-5 shadow-[6px_6px_0_0_rgba(5,5,5,1)]">
                  <p className="font-serif text-2xl text-ink">You're in.</p>
                  <p className="text-gray-700 mt-1">
                    {BETA_LIVE
                      ? "Check your inbox for your install link, then loop in."
                      : "We'll email your install link the moment the beta opens. It's days away."}
                  </p>
                </div>
              ) : (
                <>
                  <label htmlFor="beta-email" className="sr-only">
                    Email address
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      id="beta-email"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="h-14 flex-1 px-5 rounded-2xl border-2 border-ink bg-canvas text-ink placeholder:text-ink/40 focus:outline-none focus:shadow-[4px_4px_0_0_rgba(5,5,5,1)] transition-shadow"
                    />
                    {/* honeypot: hidden from real users */}
                    <input
                      type="text"
                      name="company"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      className="hidden"
                    />
                    <button
                      type="submit"
                      disabled={state === "loading" || !marketingConsent}
                      className="h-14 px-8 shrink-0 rounded-2xl bg-salmon text-on-salmon font-semibold border-2 border-ink shadow-[4px_4px_0_0_rgba(5,5,5,1)] hover:shadow-[2px_2px_0_0_rgba(5,5,5,1)] active:translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {state === "loading"
                        ? "One sec..."
                        : BETA_LIVE
                          ? "Email me the link"
                          : "Get me in"}
                    </button>
                  </div>
                  {/* C-1 (LL-0028): explicit, unticked email-marketing opt-in.
                      Required to submit; recorded server-side (sourceForm
                      "beta_access" => explicit_checkbox mechanism). */}
                  <label className="flex items-start gap-2.5 text-xs text-gray-500 mt-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      required
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-2 border-ink/30 accent-salmon focus:outline-none focus:ring-2 focus:ring-salmon"
                    />
                    <span>
                      Email me my beta install link and LetsLoop launch updates.
                      You can unsubscribe anytime.
                    </span>
                  </label>
                  <p className="text-xs text-gray-400 mt-3">
                    {BETA_LIVE
                      ? "Prefer the link by email? We'll send it straight over."
                      : "No spam, just your install link. Unsubscribe anytime."}
                  </p>
                  {state === "error" && (
                    <p className="text-sm text-red-600 mt-2">
                      Something went wrong. Please try again.
                    </p>
                  )}
                </>
              )}
            </form>
          </div>

          {/* app screenshots as phone mockups */}
          <div className="relative flex justify-center items-end h-[420px] md:h-[520px]">
            <img
              src="/screen-myloops.webp"
              width={480}
              height={1045}
              alt="The LetsLoop app showing your loops"
              className="absolute right-1/2 mr-2 bottom-2 w-40 md:w-52 rounded-[2rem] border-4 border-ink shadow-[8px_8px_0_0_rgba(5,5,5,1)] -rotate-6"
              loading="lazy"
            />
            <img
              src="/screen-loops.webp"
              width={600}
              height={1216}
              alt="The LetsLoop app showing loops happening near you"
              className="relative z-10 w-44 md:w-56 rounded-[2rem] border-4 border-ink shadow-[12px_12px_0_0_rgba(5,5,5,1)] rotate-2"
              loading="lazy"
            />
          </div>
        </div>

        {/* why join the beta */}
        <div className="grid md:grid-cols-3 gap-6 mt-24">
          {perks.map(({ Icon, title, body }) => (
            <div
              key={title}
              className="bg-canvas border-2 border-ink p-7 shadow-[6px_6px_0_0_rgba(5,5,5,1)]"
            >
              <Icon className="w-7 h-7 text-salmon mb-4" strokeWidth={2} />
              <h2 className="font-serif text-2xl text-ink mb-2">{title}</h2>
              <p className="text-gray-700 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* close */}
        <div className="mt-24 bg-paper-tan border-2 border-ink p-10 md:p-14 flex flex-col items-center text-center shadow-[8px_8px_0_0_rgba(5,5,5,1)]">
          <h2 className="font-serif text-3xl md:text-5xl text-ink mb-3">
            Loop in early.
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-md">
            Free, London-wide, iOS &amp; Android. The fewest taps between you
            and your next plan.
          </p>
          {BETA_LIVE ? (
            <AppDownload
              showQr
              align="center"
              appStoreUrl={TESTFLIGHT_URL}
              playStoreUrl={PLAY_BETA_URL}
              qrHref={BETA_URL}
              qrSrc="/beta-qr.png"
              qrAlt="QR code to join the LetsLoop beta"
              qrCaption="Scan to join the beta"
              appStoreTopLine="Install via"
              appStoreBottomLine="TestFlight"
              playTopLine="Install via"
              playBottomLine="Play Beta"
            />
          ) : (
            <p className="text-sm font-semibold text-ink/60">
              Add your email above and you're on the list, no waitlist screens.
            </p>
          )}
        </div>
      </div>
    </EditorialPage>
  );
};

export default BetaPage;
